import React, { useState, useEffect } from "react";
import { Mail, X, Clipboard } from "lucide-react";
import { toast } from "react-hot-toast";
import { useCase } from "../../context/CaseContext";

const EmailGenerator: React.FC = () => {
  const { showEmailModal, setShowEmailModal, generateEmailPreview } = useCase();

  const [emailContent, setEmailContent] = useState("");
  const [emailLang, setEmailLang] = useState<"en" | "nl">("en");
  const [emailTemplate, setEmailTemplate] = useState<
    "kickoff" | "status" | "handover"
  >("kickoff");

  // Update email content when modal opens or template settings change
  useEffect(() => {
    if (showEmailModal) {
      setEmailContent(generateEmailPreview(emailLang, emailTemplate));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showEmailModal, emailLang, emailTemplate]);

  if (!showEmailModal) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden border border-gray-200 dark:border-slate-800 animate-in zoom-in-95 flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="p-4 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900 shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-slate-800 rounded-lg text-blue-600 dark:text-blue-400">
              <Mail size={20} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                Email Generator
              </h2>
              <p className="text-xs text-slate-500">
                Generate and edit client updates
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowEmailModal(false)}
            className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Controls */}
        <div className="p-4 bg-gray-50 dark:bg-slate-950/50 border-b border-gray-100 dark:border-slate-800 flex flex-wrap gap-4 shrink-0">
          <div className="flex bg-white dark:bg-slate-900 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
            <button
              onClick={() => setEmailLang("en")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${emailLang === "en" ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-900 dark:hover:text-slate-300"}`}
            >
              English
            </button>
            <button
              onClick={() => setEmailLang("nl")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${emailLang === "nl" ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-900 dark:hover:text-slate-300"}`}
            >
              Dutch
            </button>
          </div>
          <div className="flex bg-white dark:bg-slate-900 rounded-lg p-1 border border-gray-200 dark:border-slate-700">
            <button
              onClick={() => setEmailTemplate("kickoff")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${emailTemplate === "kickoff" ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-900 dark:hover:text-slate-300"}`}
            >
              Kickoff
            </button>
            <button
              onClick={() => setEmailTemplate("status")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${emailTemplate === "status" ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-900 dark:hover:text-slate-300"}`}
            >
              Status
            </button>
            <button
              onClick={() => setEmailTemplate("handover")}
              className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${emailTemplate === "handover" ? "bg-blue-600 text-white shadow-sm" : "text-gray-500 hover:text-gray-900 dark:hover:text-slate-300"}`}
            >
              Handover
            </button>
          </div>
        </div>

        {/* Editor Area */}
        <div className="flex-1 p-0 relative">
          <textarea
            className="w-full h-full p-6 text-sm bg-white dark:bg-slate-900 dark:text-slate-300 font-mono resize-none focus:outline-none leading-relaxed"
            value={emailContent}
            onChange={(e) => setEmailContent(e.target.value)}
            placeholder="Select a template to generate email..."
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 dark:border-slate-800 bg-gray-50 dark:bg-slate-950/50 flex justify-between items-center shrink-0">
          <span className="text-xs text-gray-400">
            Edits are temporary and reset when changing templates.
          </span>
          <div className="flex gap-3">
            <button
              onClick={() => setShowEmailModal(false)}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 font-bold hover:text-slate-900 dark:hover:text-white transition-colors text-sm"
            >
              Close
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(emailContent);
                toast.success("Email copied to clipboard!");
                setShowEmailModal(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-sm flex items-center gap-2 transition-all"
            >
              <Clipboard size={14} /> Copy to Clipboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailGenerator;
